/**
 * useFreshActivePageContext
 *
 * Listens for `xavior:active-page-changed` CustomEvents and maintains BOTH:
 *   - React state (so components re-render when the active page changes)
 *   - A ref updated SYNCHRONOUSLY in the event handler (so send callbacks
 *     always read the freshest value, even when React has not yet flushed
 *     the state update through a re-render cycle)
 *
 * The staleness race this closes:
 *   xavior:active-page-changed fires → setActivePageContext() called →
 *   React BATCHES the state update → user hits Enter before re-render →
 *   handleSubmit / sendMessage reads the OLD state value.
 *
 * By updating the ref synchronously inside the event handler (before
 * returning to the React scheduler), any send callback that reads
 * `ref.current` will see the absolute latest value.
 *
 * Usage in ChatBox:
 *   const { state: activePageContext, ref: activePageContextRef } =
 *     useFreshActivePageContext();
 *
 * At send time, read `activePageContextRef.current` (not the state variable).
 */

import { useEffect, useRef, useState } from 'react';

export type ActivePageContext = {
  pageId: string;
  rawId: string;
  source: string;
  title: string;
  moduleType: string;
  focusedSections?: Array<{ sectionId: string; label: string; selector?: string }>;
};

export type UseFreshActivePageContextResult = {
  /** React state — use for rendering (triggers re-renders on change). */
  state: ActivePageContext | null;
  /**
   * Mutable ref — use at send time to read the freshest value.
   * Updated synchronously in the event handler, before React flushes
   * the corresponding state update.
   */
  ref: React.MutableRefObject<ActivePageContext | null>;
};

export function useFreshActivePageContext(): UseFreshActivePageContextResult {
  const [state, setState] = useState<ActivePageContext | null>(null);
  const ref = useRef<ActivePageContext | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail: ActivePageContext | null = (e as CustomEvent).detail ?? null;
      // Update the ref FIRST — synchronously, before the React setState
      // batch flush. Any send callback that reads ref.current after this
      // point will see the new value, even if the component hasn't re-rendered.
      ref.current = detail;
      setState(detail);
    };

    window.addEventListener('xavior:active-page-changed', handler);
    return () => window.removeEventListener('xavior:active-page-changed', handler);
  }, []);

  return { state, ref };
}
